import { useEffect, useRef, useState, useCallback } from "react";
import { Spin, Alert, DatePicker } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { getMapGeoJSON, GeoJSONCollection } from "../../services/pipeline";
import useLoading from "../../hooks/useLoading";
import {
  buildLegendTicks,
  buildTransectColorExpression,
  buildZoomRadiusExpression,
  buildZoomStrokeExpression,
  calculateMapStats,
  describeTransectValue,
  getAvailableDateRange,
  getBiomassRange,
  MapDateRange,
  withFilteredFeatures,
} from "./mapUtils";

const { RangePicker } = DatePicker;
dayjs.extend(utc);

function BiomassMap({ index }: { index: number }) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const [allGeojson, setAllGeojson] = useState<GeoJSONCollection | null>(null);
  const [geojson, setGeojson] = useState<GeoJSONCollection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, biomasa: 0 });
  const [dateRange, setDateRange] = useState<MapDateRange>(null);
  const loading = useLoading(true);

  const [availableDates, setAvailableDates] = useState<[string | null, string | null]>([null, null]);
  const [latestDate, setLatestDate] = useState<string | null>(null);
  const [generatedAt, setGeneratedAt] = useState<string | null>(null);
  const [visibleBiomassRange, setVisibleBiomassRange] = useState<[number, number]>([0, 1]);
  const [isLegendMinimized, setIsLegendMinimized] = useState(false);

  const mapType = index === 1 ? "point" : "transect";

  // Fetch GeoJSON data from the pipeline
  const fetchData = useCallback(async () => {
    loading.start();
    setError(null);
    try {
      const data = await getMapGeoJSON(mapType as "point" | "transect");
      setAllGeojson(data);
      setDateRange(null);
      const range = getAvailableDateRange(data.features);
      setAvailableDates(range);
      setLatestDate(data.metadata?.data_end || range[1]);
      setGeneratedAt(data.metadata?.generated_at || null);
      
    } catch (err: any) {
      console.error("Error fetching GeoJSON:", err);
      setError(
        err?.response?.status === 401
          ? "Sin autorización. Inicia sesión nuevamente."
          : "No se pudieron cargar los datos del mapa. El pipeline podría no haberse ejecutado aún."
      );
    } finally {
      loading.stop();
    }
  }, [mapType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Local filter effect
  useEffect(() => {
    if (!allGeojson) return;
    
    const filtered = withFilteredFeatures(allGeojson, dateRange);
    setGeojson(filtered);
    setStats(calculateMapStats(filtered.features));
    setVisibleBiomassRange(getBiomassRange(filtered.features));

  }, [allGeojson, dateRange]);

  // Initialize or update MapLibre map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Initialize the map only once
    if (!mapRef.current) {
      mapRef.current = new maplibregl.Map({
        container: mapContainer.current,
        style: {
          version: 8,
          name: "Albor Ocean",
          sources: {
            "osm-tiles": {
              type: "raster",
              tiles: [
                "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
              ],
              tileSize: 256,
              attribution:
                '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            },
          },
          layers: [
            {
              id: "osm-tiles",
              type: "raster",
              source: "osm-tiles",
              minzoom: 0,
              maxzoom: 19,
            },
          ],
        },
        center: [-79.5, 8.5], // Panama fishing zone
        zoom: 7,
        maxZoom: 18,
        minZoom: 3,
      });

      // Add navigation controls
      mapRef.current.addControl(
        new maplibregl.NavigationControl({ showCompass: true }),
        "top-right"
      );

      // Add scale bar
      mapRef.current.addControl(
        new maplibregl.ScaleControl({ maxWidth: 200 }),
        "bottom-left"
      );
    }

    return () => {
      // Don't destroy the map on re-render, only on unmount
    };
  }, []);

  // Update map data when GeoJSON changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !geojson) return;

    let handlePointClick: ((event: any) => void) | undefined;
    let handlePointMouseEnter: (() => void) | undefined;
    let handlePointMouseLeave: (() => void) | undefined;

    const addDataToMap = () => {
      // Remove existing layers/sources if they exist
      if (map.getLayer("biomass-points")) map.removeLayer("biomass-points");
      if (map.getLayer("biomass-labels")) map.removeLayer("biomass-labels");
      if (map.getSource("biomass-data")) map.removeSource("biomass-data");

      if (geojson.features.length === 0) return;

      const [minimumBiomass, maximumBiomass] = getBiomassRange(geojson.features);

      // Add GeoJSON source
      map.addSource("biomass-data", {
        type: "geojson",
        data: geojson as any,
      });

      // Add circle layer with data-driven styling
      map.addLayer({
        id: "biomass-points",
        type: "circle",
        source: "biomass-data",
        paint: {
          "circle-color": mapType === "point" ? [
            "step",
            ["to-number", ["get", "biomasa"]],
            "#ffffff", // < 1
            1, "#a3e635", // 1 - 10
            10, "#22c55e", // 10 - 20
            20, "#16a34a", // 20 - 40
            40, "#facc15", // 40 - 60
            60, "#fb923c", // 60 - 80
            80, "#ef4444"  // >= 80
          ] : buildTransectColorExpression(minimumBiomass, maximumBiomass) as any,
          "circle-radius": buildZoomRadiusExpression() as any,
          "circle-opacity": 0.7,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": buildZoomStrokeExpression() as any,
        },
      });

      // Interactive popup on click
      handlePointClick = (e: any) => {
        if (!e.features || e.features.length === 0) return;
        const props = e.features[0].properties;
        const coords = e.features[0].geometry.coordinates.slice();

        const biomassVal = `${Number(props.biomasa).toLocaleString("es-PE")} t`;

        let displayCategory = props.categoria;
        let displayColor = props.color || '#3b82f6';

        if (mapType === "transect") {
          const b = Number(props.biomasa);
          const band = describeTransectValue(b, minimumBiomass, maximumBiomass);
          displayCategory = band.label;
          displayColor = band.color;
        } else if (mapType === "point") {
          const b = Number(props.biomasa);
          if (b < 1) { displayCategory = "0 - 1"; displayColor = "#ffffff"; }
          else if (b < 10) { displayCategory = "1 - 10"; displayColor = "#a3e635"; }
          else if (b < 20) { displayCategory = "10 - 20"; displayColor = "#22c55e"; }
          else if (b < 40) { displayCategory = "20 - 40"; displayColor = "#16a34a"; }
          else if (b < 60) { displayCategory = "40 - 60"; displayColor = "#facc15"; }
          else if (b < 80) { displayCategory = "60 - 80"; displayColor = "#fb923c"; }
          else { displayCategory = "> 80"; displayColor = "#ef4444"; }
        }

        new maplibregl.Popup({
          closeButton: true,
          maxWidth: "280px",
        })
          .setLngLat(coords)
          .setHTML(`
            <div style="font-family: 'Inter', sans-serif; padding: 4px 0;">
              <div style="font-weight: 700; font-size: 14px; color: #1e293b; margin-bottom: 8px; border-bottom: 2px solid ${displayColor}; padding-bottom: 4px;">
                📍 Punto de Medición 
              </div>
              <table style="width: 100%; font-size: 12px; border-collapse: collapse;">
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">📅 Fecha</td>
                  <td style="font-weight: 600; text-align: right;">${props.fecha}</td>
                </tr>
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">🐟 Biomasa</td>
                  <td style="font-weight: 600; text-align: right;">${biomassVal}</td>
                </tr>
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">📊 Categoría</td>
                  <td style="font-weight: 600; text-align: right; color: #1e293b;">
                    ${displayCategory}
                  </td>
                </tr>
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">📡 NASC</td>
                  <td style="font-weight: 600; text-align: right;">${Number(props.nasc_total).toFixed(1)}</td>
                </tr>
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">🌊 Batimetría</td>
                  <td style="font-weight: 600; text-align: right;">${props.bati} m</td>
                </tr>
                <tr>
                  <td style="color: #64748b; padding: 2px 0;">📐 Coordenadas</td>
                  <td style="font-weight: 600; text-align: right; font-size: 11px;">${coords[1].toFixed(4)}, ${coords[0].toFixed(4)}</td>
                </tr>
              </table>
            </div>
          `)
          .addTo(map);
      };

      // Change cursor on hover
      handlePointMouseEnter = () => {
        map.getCanvas().style.cursor = "pointer";
      };
      handlePointMouseLeave = () => {
        map.getCanvas().style.cursor = "";
      };

      map.on("click", "biomass-points", handlePointClick);
      map.on("mouseenter", "biomass-points", handlePointMouseEnter);
      map.on("mouseleave", "biomass-points", handlePointMouseLeave);

      // Fit bounds to data if there are features
      if (geojson.features.length > 0) {
        const bounds = new maplibregl.LngLatBounds();
        geojson.features.forEach((f) => {
          bounds.extend(f.geometry.coordinates as [number, number]);
        });
        map.fitBounds(bounds, { padding: 60, maxZoom: 12 });
      }
    };

    if (map.isStyleLoaded()) {
      addDataToMap();
    } else {
      map.on("load", addDataToMap);
    }

    return () => {
      map.off("load", addDataToMap);
      if (handlePointClick) {
        map.off("click", "biomass-points", handlePointClick);
      }
      if (handlePointMouseEnter) {
        map.off("mouseenter", "biomass-points", handlePointMouseEnter);
      }
      if (handlePointMouseLeave) {
        map.off("mouseleave", "biomass-points", handlePointMouseLeave);
      }
    };
  }, [geojson, mapType]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const height = window.innerHeight - 40;

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      {/* Loading overlay */}
      {loading.loading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(4px)",
          }}
        >
          <Spin size="large" tip="Cargando datos del mapa..." />
        </div>
      )}

      {/* Error message */}
      {error && (
        <Alert
          message="Error al cargar datos"
          description={error}
          type="warning"
          showIcon
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            maxWidth: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
          closable
        />
      )}

      {/* Date Filter */}
      <div 
        style={{
          position: "absolute",
          top: 16,
          right: 50,
          zIndex: 5,
          background: "rgba(255, 255, 255, 0.92)",
          backdropFilter: "blur(8px)",
          borderRadius: 8,
          padding: "8px 12px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
        }}
      >
        <span style={{ marginRight: 8, fontWeight: 600, fontSize: 13, color: "#1e293b" }}>Filtrar por fecha:</span>
        <RangePicker 
          style={{ width: 260 }}
          value={dateRange as any}
          onChange={(dates) => setDateRange(dates as any)}
          allowClear
          format="YYYY-MM-DD"
        />
      </div>

      {/* Legend overlay */}
      <div
        style={{
          position: "absolute",
          bottom: 40,
          left: 10,
          zIndex: 5,
          background: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(8px)",
          padding: "16px 20px",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          minWidth: isLegendMinimized ? "auto" : (mapType === 'transect' ? 380 : 420),
        }}
      >
        <div style={{ marginBottom: isLegendMinimized ? 0 : 12, borderBottom: isLegendMinimized ? "none" : "1px solid #e2e8f0", paddingBottom: isLegendMinimized ? 0 : 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16, color: "#0f172a", fontWeight: 600, paddingRight: 16 }}>Biomasa ({mapType === 'point' ? 'Puntos' : 'Transectos'})</h3>
            <button 
              onClick={() => setIsLegendMinimized(!isLegendMinimized)}
              style={{
                background: "transparent", border: "1px solid #cbd5e1", cursor: "pointer", 
                fontSize: 16, color: "#475569", display: "flex", alignItems: "center", justifyContent: "center",
                width: 24, height: 24, borderRadius: 4, padding: 0, flexShrink: 0, fontWeight: "bold"
              }}
              title={isLegendMinimized ? "Expandir" : "Minimizar"}
            >
              {isLegendMinimized ? "+" : "−"}
            </button>
          </div>
          {!isLegendMinimized && (
            <>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 8, display: "flex", justifyContent: "space-between" }}>
                <span>Total biomasa:</span>
                <strong style={{ color: "#0f172a" }}>{stats.biomasa.toLocaleString()} t</strong>
              </div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, display: "flex", justifyContent: "space-between" }}>
                <span>Total lecturas:</span>
                <strong style={{ color: "#0f172a" }}>{stats.total.toLocaleString()}</strong>
              </div>
              {availableDates[0] && latestDate && (
                <div style={{ fontSize: 11, color: "#475569", marginTop: 6 }}>
                  Datos: {dayjs(availableDates[0].replace(/\//g, "-")).format("DD/MM/YYYY")} - {dayjs(latestDate.replace(/\//g, "-")).format("DD/MM/YYYY")}
                </div>
              )}
              {generatedAt && (
                <div style={{ fontSize: 11, color: "#475569", marginTop: 2 }}>
                  Procesado: {dayjs.utc(generatedAt).format("DD/MM/YYYY HH:mm")} UTC
                </div>
              )}
            </>
          )}
        </div>
        {!isLegendMinimized && (
          <>
            {mapType === "point" ? (
          <div style={{ marginTop: 12, paddingBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#000", fontWeight: 500, marginBottom: 2 }}>
              <span>01</span>
              <span>10</span>
              <span>20</span>
              <span>40</span>
              <span>60</span>
              <span>80</span>
              <span>100</span>
            </div>
            <div style={{ 
              height: 12, 
              width: "100%", 
              background: "linear-gradient(to right, #ffffff, #a3e635, #22c55e, #16a34a, #facc15, #fb923c, #ef4444)",
              border: "1px solid #000"
            }}></div>
            <div style={{ fontSize: 11, color: "#000", marginTop: 4 }}>Abundancia por punto (t) - Modelo 1</div>
            
            <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px 4px", fontSize: 11, color: "#475569" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#ffffff", border: "1px solid #ccc", borderRadius: "50%", flexShrink: 0 }}></span> Nulo (0-1t)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#a3e635", borderRadius: "50%", flexShrink: 0 }}></span> Muy Disperso (1-10t)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#22c55e", borderRadius: "50%", flexShrink: 0 }}></span> Disperso (10-20t)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#16a34a", borderRadius: "50%", flexShrink: 0 }}></span> Denso (20-40t)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "#facc15", borderRadius: "50%", flexShrink: 0 }}></span> Muy Denso (40-60t)</div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 10, height: 10, background: "linear-gradient(to right, #fb923c, #ef4444)", borderRadius: "50%", flexShrink: 0 }}></span> Más... (&gt;60t)</div>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 12, paddingBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#000", fontWeight: 500, marginBottom: 2 }}>
              {buildLegendTicks(visibleBiomassRange[0], visibleBiomassRange[1]).map((tick) => (
                <span key={tick}>{Math.round(tick).toLocaleString("es-PE")}</span>
              ))}
            </div>
            <div style={{ 
              height: 12, 
              width: "100%", 
              background: "linear-gradient(to right, #000080, #00ffff, #00ff00, #adff2f, #ffff00, #ffa500, #ff0000, #8b0000)",
              border: "1px solid #000"
            }}></div>
            <div style={{ fontSize: 11, color: "#000", marginTop: 4 }}>Abundancia por transecto (t) - Modelo 2</div>
          </div>
        )}
        {stats.total > 0 && (
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid #e2e8f0",
              color: "#64748b",
              fontSize: 11,
            }}
          >
            <div>📍 Puntos: <strong>{stats.total.toLocaleString()}</strong></div>
            <div>
              🐟 Total:{" "}
              <strong>{stats.biomasa.toLocaleString("es-PE")} t</strong>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Map container */}
      <div
        ref={mapContainer}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 0,
        }}
      />
    </div>
  );
}

export default BiomassMap;
